//UI for Inbound tab
import React, { Fragment } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Avatar,
  Body1Strong,
  Caption1,
  Link,
  List,
  ListItem,
  mergeClasses,
} from "@fluentui/react-components";
import { CallEnd20Filled, Call20Filled } from "@fluentui/react-icons";
import { screenPop } from "../../utils/acs.salesforce";
import Stopwatch from "../ActiveCall/Stopwatch";

const acceptBtnHoverStyles = {
  backgroundColor: tokens.colorPaletteGreenBackground3,
  color: "white",
};

const rejectBtnHoverStyles = {
  backgroundColor: tokens.colorPaletteRedForeground3,
  color: "white",
};

const amberColor = tokens.colorPaletteMarigoldForeground3;
const connectedColor = tokens.colorPaletteGreenForeground1;

const statusColor = (callState) => {
  if (callState === "Connected") return connectedColor;
  if (callState === "Connecting" || callState === "Ringing" || callState === "EarlyMedia")
    return amberColor;
  if (callState === "Disconnected") return "#d13438";
  return "#605e5c";
};

const statusLabel = (callState) => {
  switch (callState) {
    case "Ringing":
      return "Ringing…";
    case "Connecting":
      return "Connecting…";
    case "Connected":
      return "In Call";
    case "LocalHold":
      return "On Hold";
    case "RemoteHold":
      return "Held Remotely";
    case "Disconnected":
      return "Call Ended";
    default:
      return callState || "Idle";
  }
};

const useStyles = makeStyles({
  //list : start
  noCallMsg: {
    margin: "1rem",
    color: tokens.colorNeutralForeground4,
  },
  listRoot: {
    width: "auto",
    padding: 0,
  },
  listItem: {
    padding: "0.5rem 0.85rem",
    borderBottom: "1px solid var(--neutralStroke2, #e1e1e1)",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground4,
    },
  },
  listItemGrid: {/*display: "grid",*/},
  //list : end
  toastContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.borderRadius("0.5rem"),
    opacity: 0.95,
  },
  avatarContainer: {
    //marginRight: '0.5rem',
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    ...shorthands.margin(0, "8px"),
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  subText: {
    fontSize: "0.75rem",
  },
  acceptButton: {
    backgroundColor: tokens.colorStatusSuccessForeground2,
    color: "white",
    borderRadius: "2rem",
    minWidth: "2rem",
    width: "2rem",
    ":hover": acceptBtnHoverStyles,
    ":active": acceptBtnHoverStyles,
    ":hover:active": acceptBtnHoverStyles,
    ":focus": acceptBtnHoverStyles,
  },
  rejectButton: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    color: "white",
    borderRadius: "2rem",
    minWidth: "2rem",
    width: "2rem",
    ":hover": rejectBtnHoverStyles,
    ":active": rejectBtnHoverStyles,
    ":hover:active": rejectBtnHoverStyles,
    ":focus": rejectBtnHoverStyles,
  },
  nameLink: {
    color: "inherit",
    textDecoration: "none",
    minWidth: 0,
    display: "inline-block",
  },
});

function IncomingCallToast({ onClickAccept, onClickReject, incomingCallMap, showIncomingRing }) {
  const styles = useStyles();
  const asMap = (m) =>
    m instanceof Map
      ? m
      : Array.isArray(m)
        ? new Map(m)
        : m && typeof m === "object"
          ? new Map(Object.entries(m))
          : new Map();

  const incomingCallMapRev = [...asMap(incomingCallMap).values()].reverse();

  //if(showIncomingRing) {
  if (!incomingCallMapRev || incomingCallMapRev?.length === 0) {
    return (
      <div className={mergeClasses(styles.listRoot, styles.noCallMsg)}>No incoming calls...</div>
    );
  }

  return (
    <div className={styles.listRoot}>
      <List>
        {[...(incomingCallMapRev ?? new Map()).values()].map((incomingCallObj, index) => {
          const callStatusColor = statusColor(incomingCallObj.callState);
          //const callerNumber = incomingCallObj?.callerInfo?.matchedRecord?.name ? incomingCallObj?.callerInfo?.callerNumber: null; //no need

          //combined number as we do not want to show it separately
          const callerName =
            incomingCallObj.callerInfo?.matchedRecord?.name ||
            incomingCallObj.callerInfo?.callerName ||
            incomingCallObj.callerInfo?.callerNumber ||
            "Unknown";

          const recordId = incomingCallObj.callerInfo?.matchedRecord?.recordId;
          const searchParams = incomingCallObj.searchParams;

          const queueName = incomingCallObj.transferorInfo?.displayName;
          const queueText =
            incomingCallObj.transferorInfo?.callScenario === "TransferToParticipant"
              ? "Transferred by"
              : "Call for";

          const avatar = null; // fetch profile pic in future

          return (
            <Fragment key={incomingCallObj.id}>
              <ListItem className={styles.listItem}>
                <div className={styles.listItemGrid}>
                  <div className={styles.toastContainer}>
                    <div className={styles.avatarContainer}>
                      {/* Set color="brand" to match the blue background for initials */}
                      <Avatar
                        image={{ src: avatar }}
                        name={callerName}
                        size={40}
                        aria-label={callerName}
                      />
                    </div>

                    <div className={styles.textContainer}>
                      <Link
                        href="#"
                        role="button"
                        onClick={() => {
                          if (recordId) {
                            screenPop({
                              type: "SOBJECT",
                              params: { recordId },
                            });
                          } else if (searchParams) {
                            screenPop({
                              type: "SEARCH",
                              params: { searchString: searchParams },
                            });
                          }
                        }}
                        className={styles.nameLink}
                        aria-label={`Open details for ${callerName}`}
                        title={callerName}
                      >
                        <Body1Strong style={{ fontSize: "0.875rem" }}>{callerName}</Body1Strong>
                      </Link>
                      {queueName && (
                        <Caption1 className={styles.subText}>
                          {`${queueText} ${queueName}`}
                        </Caption1>
                      )}

                      <Caption1>
                        <span style={{ color: callStatusColor, fontSize: "12px", fontWeight: 600 }}>
                          {statusLabel(incomingCallObj.callState)}
                        </span>
                      </Caption1>

                      {/*<Caption1>
                          <Stopwatch answerAt={incomingCallObj?.startedAt ?? Date.now()} />
                        </Caption1>*/}
                    </div>

                    <div className={styles.buttonGroup}>
                      <Button
                        appearance="primary"
                        shape="circular"
                        icon={<CallEnd20Filled />}
                        className={styles.rejectButton}
                        onClick={() => onClickReject(incomingCallObj.id)}
                      />
                      <Button
                        appearance="primary"
                        shape="circular"
                        icon={<Call20Filled />}
                        className={styles.acceptButton}
                        onClick={() => onClickAccept(incomingCallObj.id)}
                      />
                    </div>
                  </div>
                </div>
              </ListItem>
              {index < incomingCallMap.length - 1 && <Divider appearance="subtle" />}
            </Fragment>
          );
        })}
      </List>
    </div>
  );
  //}
}

export default IncomingCallToast;
